import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const DEG = Math.PI / 180;
const centerOf = (c) => [(c.tr[0] + c.bl[0]) / 2, (c.tr[1] + c.bl[1]) / 2];

// move (dE/dN meters), rotate (deg), scale (factor) about the overlay centre
export function editCorners(c, { dE = 0, dN = 0, rotDeg = 0, scale = 1 }) {
  const ctr = centerOf(c);
  const mlat = 111320;
  const mlng = 111320 * Math.cos(ctr[0] * DEG);
  const th = rotDeg * DEG;
  const cos = Math.cos(th);
  const sin = Math.sin(th);
  const tf = (p) => {
    let E = (p[1] - ctr[1]) * mlng;
    let N = (p[0] - ctr[0]) * mlat;
    E *= scale;
    N *= scale;
    const E2 = cos * E - sin * N;
    const N2 = sin * E + cos * N;
    return [ctr[0] + (N2 + dN) / mlat, ctr[1] + (E2 + dE) / mlng];
  };
  return { tl: tf(c.tl), tr: tf(c.tr), bl: tf(c.bl) };
}

export const cornersOf = (o) => ({ tl: o.corner_tl, tr: o.corner_tr, bl: o.corner_bl });

// All overlay (PDF/image map layer) state + actions, lifted out of the UI so
// both the settings screen (full management) and the layers menu (on/off +
// opacity) can share one source of truth.
export function useHomeOverlay(mapApiRef) {
  const [overlay, setOverlay] = useState(null);
  const [corners, setCorners] = useState(null);
  const [opacity, setOpacity] = useState(0.7);
  const [visible, setVisible] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [step, setStep] = useState(10);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [askAlign, setAskAlign] = useState(false);
  const cornersRef = useRef(null);
  cornersRef.current = corners;

  const applyDoc = useCallback((o) => {
    setOverlay(o);
    if (o) {
      setCorners(cornersOf(o));
      setOpacity(o.opacity == null ? 0.7 : o.opacity);
      setVisible(o.visible !== false);
    } else {
      setCorners(null);
    }
    setDirty(false);
  }, []);

  useEffect(() => {
    let ok = true;
    api
      .getHomeOverlay()
      .then(({ data }) => ok && applyDoc(data.overlay || null))
      .catch(() => {})
      .finally(() => ok && setLoading(false));
    return () => {
      ok = false;
    };
  }, [applyDoc]);

  const runAuto = useCallback(
    async (oid) => {
      setAiBusy(true);
      try {
        // Anchor the AI alignment to the current map view so it can't jump to a
        // far-away same-named place.
        let view;
        const m = mapApiRef.current;
        if (m) {
          const c = m.getCenter();
          const vw = m.getViewWidthMeters();
          view = { centerLat: c.lat, centerLon: c.lon, spanM: Math.round(vw) };
        }
        const { data } = await api.autoPositionHomeOverlay(oid, view);
        const c = cornersOf(data.overlay);
        setCorners(c);
        setDirty(false);
        mapApiRef.current?.fitToCorners(c);
        const disp = data.meta?.center?.display;
        toast.success(
          disp
            ? `KI ausgerichtet – ${disp.split(",").slice(0, 2).join(",").trim()}`
            : "KI-Ausrichtung übernommen"
        );
      } catch (err) {
        const msg = err?.response?.data?.detail || "KI-Ausrichtung fehlgeschlagen";
        toast.error(msg);
        if (cornersRef.current) mapApiRef.current?.fitToCorners(cornersRef.current);
      } finally {
        setAiBusy(false);
      }
    },
    [mapApiRef]
  );

  const onFile = useCallback(
    async (e) => {
      const f = e.target.files?.[0];
      e.target.value = "";
      if (!f) return;
      setUploading(true);
      setUploadPct(0);
      try {
        // View anchor (so the plan lands on the current map area).
        let centerLat, centerLon, spanM;
        const m = mapApiRef.current;
        if (m) {
          const c = m.getCenter();
          const vw = m.getViewWidthMeters();
          centerLat = c.lat;
          centerLon = c.lon;
          spanM = Math.max(50, Math.round(vw * 0.6));
        }
        // Chunked upload: split into small parts so the production ingress
        // (request-body size limit) never rejects a large plan (~20 MB PDFs).
        const CHUNK = 512 * 1024;
        const total = Math.max(1, Math.ceil(f.size / CHUNK));
        const uploadId = (
          (typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `up_${Date.now()}_${Math.random().toString(36).slice(2)}`) +
          `_${Math.random().toString(36).slice(2, 8)}`
        ).replace(/[^A-Za-z0-9_-]/g, "");
        for (let i = 0; i < total; i++) {
          const blob = f.slice(i * CHUNK, (i + 1) * CHUNK);
          const fd = new FormData();
          fd.append("uploadId", uploadId);
          fd.append("index", String(i));
          fd.append("total", String(total));
          fd.append("chunk", blob, "chunk");
          await api.uploadHomeOverlayChunk(fd);
          setUploadPct(Math.round(((i + 1) / total) * 100));
        }
        const { data } = await api.completeHomeOverlay({
          uploadId,
          filename: f.name,
          total,
          centerLat,
          centerLon,
          spanM,
        });
        applyDoc(data.overlay);
        mapApiRef.current?.fitToCorners(cornersOf(data.overlay));
        toast.success("Overlay geladen");
        setAskAlign(true); // ask before running the AI auto-alignment
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Overlay konnte nicht geladen werden");
      } finally {
        setUploading(false);
        setUploadPct(0);
      }
    },
    [mapApiRef, applyDoc]
  );

  const apply = useCallback((opt) => {
    setCorners((c) => editCorners(c, opt));
    setDirty(true);
  }, []);

  const doSave = useCallback(async () => {
    if (!overlay || !corners) return;
    setSaving(true);
    try {
      await api.saveHomeOverlayCorners(overlay.id, {
        corner_tl: corners.tl,
        corner_tr: corners.tr,
        corner_bl: corners.bl,
      });
      setOverlay((o) => ({
        ...o,
        corner_tl: corners.tl,
        corner_tr: corners.tr,
        corner_bl: corners.bl,
      }));
      setDirty(false);
      toast.success("Position gespeichert");
    } catch {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }, [overlay, corners]);

  const resetToSaved = useCallback(() => {
    if (overlay) setCorners(cornersOf(overlay));
    setDirty(false);
  }, [overlay]);

  const persistProps = useCallback(
    (patch) => {
      if (overlay) api.updateHomeOverlayProps(overlay.id, patch).catch(() => {});
    },
    [overlay]
  );

  const toggleVisible = useCallback(() => {
    setVisible((v) => {
      const nv = !v;
      persistProps({ visible: nv });
      return nv;
    });
  }, [persistProps]);

  const doDelete = useCallback(async () => {
    if (!overlay) return;
    try {
      await api.deleteHomeOverlay(overlay.id);
      applyDoc(null);
      setEditMode(false);
      toast.success("Overlay entfernt");
    } catch {
      toast.error("Entfernen fehlgeschlagen");
    }
  }, [overlay, applyDoc]);

  const display = useMemo(
    () =>
      overlay && corners
        ? {
            id: overlay.id,
            imageUrl: api.homeOverlayImageUrl(overlay.id),
            corners,
            opacity,
            visible,
          }
        : null,
    [overlay, corners, opacity, visible]
  );

  const busy = uploading || aiBusy;

  return {
    overlay,
    corners,
    opacity,
    setOpacity,
    visible,
    toggleVisible,
    editMode,
    setEditMode,
    step,
    setStep,
    dirty,
    loading,
    uploading,
    uploadPct,
    saving,
    aiBusy,
    askAlign,
    setAskAlign,
    busy,
    runAuto,
    onFile,
    apply,
    doSave,
    resetToSaved,
    persistProps,
    doDelete,
    display,
  };
}
